import React from 'react'

import type { IconNames } from '@gousto-internal/citrus-react'
import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  DisplayPosition,
  Divider,
  FlexDirection,
  FontFamily,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
  Text,
} from '@gousto-internal/citrus-react'
import classnames from 'classnames'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import { boxSummaryVisibilityChange } from 'actions/boxSummary'
import { editBoxSizeOrDate } from 'actions/trackingKeys'
import { registerBoxSummaryTreatmentFocusTarget } from 'routes/Menu/components/BoxSummary/treatmentFocus'
import { getBasketDate, getBasketRecipesCount, getNumPortions } from 'selectors/basket'

import { BoxSummaryPickerContainer } from './BoxSummaryPickerContainer'
import type { BoxSummaryProps } from './__config__/boxTypes'
import { boxTypes } from './__config__/boxTypes'

import css from '../CollectionsNav.css'

const formatDateToHumanReadable = (dateString: string) => moment(dateString).format('ddd D MMM')
const basketComprehensionSection = 'information_row'
const informationBannerComponentDesign = 'information_banner'
const shoppingBasketIconComponentDesign = 'shopping_basket_icon'
const boxSummaryBasketTestId = 'box-summary-basket'

type TrackEditBoxSizeOrDateContext = {
  section?: string
  component_design?: string
}

export const trackEditBoxSizeOrDate = (
  currentSelection: string,
  trackingContext: TrackEditBoxSizeOrDateContext = {},
) => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: editBoxSizeOrDate,
    currentSelection,
    ...trackingContext,
  },
})

const BoxSummaryItem = ({ iconName, itemTitle, dataNoCode }: BoxSummaryProps) => {
  return (
    <>
      <Space direction="vertical" size={2} />
      <Box
        paddingH={2}
        paddingV={2}
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        alignItems={AlignItems.Center}
      >
        <Icon name={iconName as IconNames} />
        <Space direction="horizontal" size={2} />
        <Body2 data-no-code={dataNoCode}>{itemTitle}</Body2>
      </Box>
    </>
  )
}

const BoxSummaryBasketItem = ({ basketRecipesCount }: { basketRecipesCount: number }) => (
  <Box
    data-no-code="menuConfigBasket"
    data-testid={boxSummaryBasketTestId}
    paddingH={2}
    paddingV={2}
    paddingRight={4}
    paddingBottom={2}
    display={Display.Flex}
    flexDirection={FlexDirection.Row}
    alignItems={AlignItems.Center}
  >
    <Box position={DisplayPosition.Relative}>
      <Icon size={6} variant={IconVariant.Actionable} name="shopping_basket" />

      {basketRecipesCount > 0 && (
        <Box
          data-testid="box-summary-basket-count"
          position={DisplayPosition.Absolute}
          top="-6px"
          right="-6px"
          bg={Color.Secondary_600}
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          height="18px"
          width="18px"
          paddingH={1}
          borderRadius="round"
        >
          <Text color={Color.White} fontFamily={FontFamily.Bold} size={0}>
            {basketRecipesCount}
          </Text>
        </Box>
      )}
    </Box>
  </Box>
)

export const BoxSummaryPicker = () => {
  const dispatch = useDispatch()
  const numPortions = useSelector(getNumPortions)
  const basketDate = useSelector(getBasketDate)
  const basketRecipesCount = useSelector(getBasketRecipesCount)
  const { title } = boxTypes[numPortions]

  const formattedBasketDate = formatDateToHumanReadable(basketDate)

  const getBasketComprehensionTrackingContext = (componentDesign: string) => ({
    section: basketComprehensionSection,
    component_design: componentDesign,
  })

  const getComponentDesign = (target: EventTarget) =>
    target instanceof Element && target.closest(`[data-testid="${boxSummaryBasketTestId}"]`)
      ? shoppingBasketIconComponentDesign
      : informationBannerComponentDesign

  const toggleBoxSummary = (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    const componentDesign = event?.target
      ? getComponentDesign(event.target)
      : informationBannerComponentDesign

    if (event?.currentTarget instanceof HTMLElement) {
      registerBoxSummaryTreatmentFocusTarget(event.currentTarget)
    }

    dispatch(boxSummaryVisibilityChange(true))
    dispatch(trackEditBoxSizeOrDate(title, getBasketComprehensionTrackingContext(componentDesign)))
  }

  return (
    <Box className={classnames(css.nav)}>
      <Box paddingV={2}>
        <Divider />
      </Box>
      <BoxSummaryPickerContainer
        data-no-code="menuConfigBar"
        style={{ cursor: 'pointer' }}
        onClick={toggleBoxSummary}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggleBoxSummary(event)
          }
        }}
        role="button"
        tabIndex={0}
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceBetween}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.Center}
        >
          <BoxSummaryItem
            iconName="meals_per_box"
            itemTitle={title}
            dataNoCode="menuConfigBoxSize"
          />
          <BoxSummaryItem
            iconName="delivery"
            itemTitle={formattedBasketDate}
            dataNoCode="menuConfigDeliveryDate"
          />
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.Center}
        >
          <Space direction="horizontal" size={8} />
          <BoxSummaryBasketItem basketRecipesCount={basketRecipesCount} />
        </Box>
      </BoxSummaryPickerContainer>
    </Box>
  )
}
